// Copyright 2020 Northern.tech AS
//
//    Licensed under the Apache License, Version 2.0 (the "License");
//    you may not use this file except in compliance with the License.
//    You may obtain a copy of the License at
//
//        http://www.apache.org/licenses/LICENSE-2.0
//
//    Unless required by applicable law or agreed to in writing, software
//    distributed under the License is distributed on an "AS IS" BASIS,
//    WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//    See the License for the specific language governing permissions and
//    limitations under the License.
import { Typography } from '@mui/material';

import DeviceIdentityDisplay from '@northern.tech/common-ui/DeviceIdentity';
import { Link } from '@northern.tech/common-ui/Link';
import TextOverflowMultiline from '@northern.tech/common-ui/TextOverflowMultiline';
import Time from '@northern.tech/common-ui/Time';
import { DEPLOYMENT_ROUTES, auditlogTypes, canAccess } from '@northern.tech/store/constants';
import { formatReleases, generateReleasesPath } from '@northern.tech/store/locationutils';

import { stringifyEvent } from './eventdetails/utils';

const InternalLink = ({ children, to }) => (
  <Link state={{ internal: true }} to={to}>
    {children}
  </Link>
);

const SoftwareLink = ({ item }) => {
  const { artifact, id, software } = item.object;
  if (software?.kind === 'manifest') {
    return (
      <InternalLink
        to={{ pathname: generateReleasesPath({ pageState: {} }), search: formatReleases({ pageState: { tab: 'manifests', id: encodeURIComponent(id) } }) }}
      >
        View software
      </InternalLink>
    );
  }
  return (
    <InternalLink
      to={{
        pathname: generateReleasesPath({ pageState: { selectedRelease: artifact?.name ?? id } }),
        search: formatReleases({ pageState: { tab: 'releases' } })
      }}
    >
      View software
    </InternalLink>
  );
};
const DeploymentLink = ({ item }) => <InternalLink to={`${DEPLOYMENT_ROUTES.finished.route}?open=true&id=${item.object.id}`}>View deployment</InternalLink>;
const DeviceLink = ({ item }) => <InternalLink to={`/devices?id=${item.object.id}`}>View device</InternalLink>;
const DeviceRejectedLink = ({ item }) => <InternalLink to={`/devices/rejected?id=${item.object.id}`}>View device</InternalLink>;
const TerminalSessionLink = () => <Link>View session log</Link>;
const ChangeFallback = props => {
  const {
    item: { change = '-' }
  } = props;
  return <TextOverflowMultiline variant="body2">{change}</TextOverflowMultiline>;
};
const EmptyComponent = () => <div />;

const FallbackFormatter = props => <Typography variant="body2">{stringifyEvent(props)}</Typography>;

const ArtifactFormatter = ({ artifact }) => <TextOverflowMultiline variant="body2">{artifact.name}</TextOverflowMultiline>;
const SoftwareFormatter = ({ id }) => <TextOverflowMultiline variant="body2">{id}</TextOverflowMultiline>;
const DeploymentFormatter = ({ deployment }) => <TextOverflowMultiline variant="body2">{deployment.name}</TextOverflowMultiline>;
const DeviceFormatter = ({ id }) => <DeviceIdentityDisplay device={{ id }} />;
const TokenFormatter = ({ id, user_access_token: token }) => <TextOverflowMultiline variant="body2">{token?.name ?? id}</TextOverflowMultiline>;
const UserFormatter = ({ user }) => <TextOverflowMultiline variant="body2">{user.email}</TextOverflowMultiline>;
const TenantFormatter = ({ tenant }) => <TextOverflowMultiline variant="body2">{tenant.name}</TextOverflowMultiline>;

const defaultAccess = canAccess;
const changeMap = {
  default: { component: EmptyComponent, actionFormatter: FallbackFormatter, title: 'defaultTitle', accessCheck: defaultAccess },
  artifactDeleted: { actionFormatter: ArtifactFormatter, component: EmptyComponent, accessCheck: defaultAccess },
  artifactGeneral: { actionFormatter: ArtifactFormatter, component: SoftwareLink, accessCheck: ({ canReadReleases }) => canReadReleases },
  softwareDeleted: { actionFormatter: SoftwareFormatter, component: EmptyComponent, accessCheck: defaultAccess },
  softwareGeneral: { actionFormatter: SoftwareFormatter, component: SoftwareLink, accessCheck: ({ canReadReleases }) => canReadReleases },
  deployment: {
    actionFormatter: DeploymentFormatter,
    component: DeploymentLink,
    accessCheck: ({ canReadDeployments }) => canReadDeployments
  },
  deviceDecommissioned: { actionFormatter: DeviceFormatter, component: EmptyComponent, accessCheck: defaultAccess },
  deviceRejected: { actionFormatter: DeviceFormatter, component: DeviceRejectedLink, accessCheck: ({ canReadDevices }) => canReadDevices },
  deviceGeneral: { actionFormatter: DeviceFormatter, component: DeviceLink, accessCheck: ({ canReadDevices }) => canReadDevices },
  deviceTerminalSession: { actionFormatter: DeviceFormatter, component: TerminalSessionLink, accessCheck: defaultAccess },
  user: { actionFormatter: UserFormatter, component: ChangeFallback, accessCheck: defaultAccess },
  user_access_token: { actionFormatter: TokenFormatter, component: ChangeFallback, accessCheck: defaultAccess },
  tenant: { actionFormatter: TenantFormatter, component: ChangeFallback, accessCheck: defaultAccess }
};

const mapChangeToContent = item => {
  let content = changeMap[item.object.type];
  if (content) {
    return content;
  } else if (item.object.type === 'artifact' && item.action === 'delete') {
    content = changeMap.artifactDeleted;
  } else if (item.object.type === 'artifact') {
    content = changeMap.artifactGeneral;
  } else if (item.object.type === 'software' && item.action === 'delete') {
    content = changeMap.softwareDeleted;
  } else if (item.object.type === 'software') {
    content = changeMap.softwareGeneral;
  } else if (item.object.type === 'device' && item.action.includes('terminal')) {
    content = changeMap.deviceTerminalSession;
  } else if (item.object.type === 'device' && item.action.includes('reject')) {
    content = changeMap.deviceRejected;
  } else if (item.object.type === 'device' && item.action.includes('decommission')) {
    content = changeMap.deviceDecommissioned;
  } else if (item.object.type === 'device') {
    content = changeMap.deviceGeneral;
  } else {
    content = changeMap.default;
  }
  return content;
};

const actorMap = {
  user: 'email',
  device: 'id'
};

export const UserDescriptor = (item, index) => (
  <TextOverflowMultiline key={`${item.time}-${index}`} variant="body2">
    {item.actor[actorMap[item.actor.type]]}
  </TextOverflowMultiline>
);
export const ActionDescriptor = (item, index) => (
  <TextOverflowMultiline className="uppercased" key={`${item.time}-${index}`} variant="body2">
    {item.action}
  </TextOverflowMultiline>
);
export const TypeDescriptor = (item, index) => (
  <TextOverflowMultiline className="capitalized" key={`${item.time}-${index}`} variant="body2">
    {auditlogTypes[item.object.type]?.title ?? item.object.type}
  </TextOverflowMultiline>
);
export const ChangeDescriptor = (item, index) => {
  const FormatterComponent = mapChangeToContent(item).actionFormatter;
  return <FormatterComponent key={`${item.time}-${index}`} {...item.object} />;
};
export const ChangeDetailsDescriptor = (item, index, userCapabilities) => {
  const { component: Comp, accessCheck } = mapChangeToContent(item);
  const key = `${item.time}-${index}`;
  return accessCheck(userCapabilities) ? <Comp key={key} item={item} /> : <div key={key} />;
};
export const TimeWrapper = (item, index) => <Time key={`${item.time}-${index}`} value={item.time} Component={Typography} component="time" variant="body2" />;
